import { getBearing, getGeomLength, pointsToEN } from '@openlayers/ol-utils';
import { Ajax2 } from '@shared/Ajax';
import { Auth } from '@shared/Auth';
import { ImagesService } from '@shared/images/ImagesService';
import { Catch } from '@shared/ui-react/Catch';
import { getFilenameWithVersion } from '@shared/utils/utils';
import ImageViewer from 'iv-viewer';
import 'iv-viewer/dist/iv-viewer.css';
import { LineString } from 'ol/geom';
import * as React from 'react';
import './ImagePanoViewer.scss';
import { DateSpan, DateTimeSpan } from './UtilComponents';
import { LStorage } from '@shared/Storage';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

declare const pannellum: any;
declare const kuura: any;
declare const moment: any;

/*
 * Loads either regular <img> or 360 photo viewer
 * Optionally shows image info
 * Requires full component rerender
 */
export class ImagePanoViewer extends React.Component<
    {
        image: IImage;
        showInfo?: 'top' | 'bottom' | false;
        getAllPanoramas: () => IImage[];
        openImageId: (id: number) => void;
        isMobile: boolean;
    },
    {
        loading: boolean;
        isMultires: boolean;
        multiresConfig: any;
    }
> {
    panoViewer: any = null; // pannellum instance
    imageViewer: any = null; // imageviewer instance
    wrapperEl: HTMLDivElement = null;
    imgContainerEl: HTMLDivElement = null;
    panoContainerEl: HTMLDivElement = null;
    resizeObserver: ResizeObserver = null;

    wrapperCss: any = {
        display: 'flex',
        flexDirection: 'column',
        flex: 'auto',
        overflow: 'hidden',
    } as React.CSSProperties;

    componentDidMount() {
        setTimeout(() => {
            try {
                this.resizeObserver = new ResizeObserver(
                    _.throttle((entries, observer) => {
                        if (this.imageViewer) {
                            this.imageViewer.refresh();
                        } else if (this.panoViewer) {
                            this.panoViewer.resize();
                        }
                    }, 50)
                );
                this.resizeObserver.observe(this.wrapperEl);
            } catch {}
        }, 100);

        this.init();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.image.uuid !== this.props.image.uuid) {
            this.init();
        }
    }

    componentWillUnmount() {
        try {
            if (this.panoViewer) {
                this.panoViewer.destroy();
            }
            if (this.imageViewer) {
                this.imageViewer.destroy();
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        } catch {}
    }

    async init() {
        if (this.props.image.panorama) {
            await this.checkIfMultires();
            this.initPanorama();
        } else {
            this.initPanAndZoom();
        }
    }

    render() {
        const { image, showInfo } = this.props;

        return (
            <div style={this.wrapperCss} ref={el => (this.wrapperEl = el)}>
                {showInfo === 'top' && (
                    <Catch>
                        <ImageViewerInfo image={image} position={showInfo} />{' '}
                    </Catch>
                )}

                {image.panorama ? (
                    <Catch>
                        <div id='panorama-wrapper' ref={el => (this.panoContainerEl = el)} style={{ flex: '1 1 auto' }}></div>
                    </Catch>
                ) : (
                    <Catch>
                        <div id='img-viewer' ref={el => (this.imgContainerEl = el)} style={{ flex: '1 1 auto', height: this.props.isMobile ? '50vh' : '100%' }}></div>
                    </Catch>
                )}

                {showInfo === 'bottom' && (
                    <Catch>
                        <ImageViewerInfo image={image} position={showInfo} />
                    </Catch>
                )}
            </div>
        );
    }

    checkIfMultires = async () => {
        const projectUuid = kuura.getProjectUuid();
        const imageUuid = this.props.image.uuid;
        // TODO set domain in kuura vars
        const domain = 'https://tiles.maps.infrakit.com';
        const url = `${domain}/360-images/v1/proj/${projectUuid}/doc/${imageUuid}/config.json`;

        try {
            const response = await Ajax2({
                url: url,
                method: 'GET',
                useToken: true,
                contentType: 'json',
            });

            if (response && !response.errorMsg) {
                const config = response;
                config.url = `${domain}/360-images/v1/proj/${projectUuid}/doc/${imageUuid}`;
                this.setState({
                    multiresConfig: config,
                    isMultires: true,
                });
            } else {
                console.log('config.json does not exist or failed to load.');
                this.setState({ isMultires: false });
            }
        } catch (error) {
            console.error('There was an error accessing the URL:', error);
            this.setState({ isMultires: false });
        }
    };

    initPanorama = () => {
        const tryInit = (imageSize: 'o' | '4k' | '2k') => {
            if (!this.panoContainerEl) return;
            if (this.panoViewer) {
                this.panoViewer.destroy();
            }

            const safeDirection = Math.max(0, this.props.image.direction ?? 0);
            const hotSpots = [];
            if (this.props.image.geographicPoint?.lat && this.props.image.geographicPoint?.lon) {
                (this.props.getAllPanoramas ? this.props.getAllPanoramas() : []).forEach(p => {
                    if (p.geographicPoint?.lat && p.uuid != this.props.image.uuid) {
                        const distance = getGeomLength(new LineString(pointsToEN([p.geographicPoint, this.props.image.geographicPoint])), 'EPSG:3857');
                        const az = getBearing(this.props.image.geographicPoint.lat, this.props.image.geographicPoint.lon, p.geographicPoint.lat, p.geographicPoint.lon);
                        hotSpots.push({
                            id: p.id,
                            pitch: 5,
                            yaw: az - safeDirection,
                            type: 'scene',
                            text: distance.toFixed(1) + 'm - ' + p.name,
                            distance,
                            clickHandlerFunc: x => this.props.openImageId(p.id),
                        });
                    }
                });
            }

            if (this.imageViewer) {
                try {
                    this.imageViewer.destroy();
                    this.imageViewer = null;
                } catch {}
            }
            if (this.state?.isMultires) {
                let IAuthToken = Auth.getStoredToken();
                if (Auth.isTokenExpired(IAuthToken)) {
                    Auth.refreshToken();
                    IAuthToken = Auth.getStoredToken();
                }
                this.panoViewer = pannellum.viewer(this.panoContainerEl, {
                    hfov: this.state.multiresConfig.hfov,
                    type: 'multires',
                    multiRes: {
                        basePath: this.state.multiresConfig.url,
                        path: this.state.multiresConfig.multiRes.path,
                        fallbackPath: this.state.multiresConfig.multiRes.fallbackPath,
                        extension: `${this.state.multiresConfig.multiRes.extension}?token=${IAuthToken['accessToken']}`,
                        tileResolution: this.state.multiresConfig.multiRes.tileResolution,
                        maxLevel: this.state.multiresConfig.multiRes.maxLevel,
                        cubeResolution: this.state.multiresConfig.multiRes.cubeResolution,
                    },
                    autoLoad: true,
                    compass: true,
                    northOffset: safeDirection,
                    hotSpots: [],
                });
            } else {
                this.panoViewer = pannellum.viewer(this.panoContainerEl, {
                    type: 'equirectangular',
                    panorama: ImagesService.getFullImageUrl(this.props.image.id, imageSize),
                    autoLoad: true,
                    compass: true,
                    northOffset: safeDirection,
                    hotSpots: [],
                });
            }

            this.hotspots = hotSpots;
            this.renderHotspots();

            if (!document.querySelector('.pnlm-hotspots-toggle')) {
                const ctlsEl = document.querySelector('.pnlm-controls-container');
                const hotspotsToggleButton = document.createElement('div');
                hotspotsToggleButton.className = 'pnlm-controls pnlm-control pnlm-hotspots-toggle flex-row centered';
                hotspotsToggleButton.innerHTML = `<i class="iki iki-arrow-up" style="font-size: 22px"></i>
<div class="pnlm-controls pnlm-control pnlm-hotspots-toggle">
<label style="margin-right: 10px">${__('js_photo.panorama_viewer.hotspots_range')}</label>
<button class="ik-button-secondary" data-hotspot-range="0">Off</button>
<button class="ik-button-secondary" data-hotspot-range="150">150m</button>
<button class="ik-button-secondary" data-hotspot-range="300">300m</button>
<button class="ik-button-secondary" data-hotspot-range="1000">1km</button>
</div>`;
                hotspotsToggleButton.style.padding = '2px 0';
                hotspotsToggleButton.addEventListener('click', e => {
                    const el = e.target as HTMLDivElement;
                    const range = el.dataset.hotspotRange;
                    LStorage.setItem(this.hotspotsRangeStorageKey, range);
                    this.renderHotspots();
                });
                ctlsEl.appendChild(hotspotsToggleButton);
            }

            this.panoViewer.on('error', error => {
                if (imageSize === 'o') {
                    tryInit('4k');
                } else if (imageSize === '4k') {
                    tryInit('2k');
                }
            });
        };

        if (typeof pannellum === 'undefined') {
            // lazy load
            jQuery.getScript('//web.infrakit.com/lib/pannellum_2.5.6/pannellum.js', () => {
                this.initPanorama();
            });
        } else {
            tryInit('o');
        }
    };

    hotspots: any[] = [];
    hotspotsRangeStorageKey = 'PANORAMA_HOTSPOTS_RANGE';
    hotspotsRangeDefault = 150;

    renderHotspots = () => {
        if (!this.panoViewer) return;
        const hotspotRange = LStorage.getOrDefault(
            this.hotspotsRangeStorageKey,
            kuura.getDefaultPanoramaHotspotRadius() != null ? kuura.getDefaultPanoramaHotspotRadius() : this.hotspotsRangeDefault
        );

        this.hotspots.forEach(hs => {
            if (hs.distance <= hotspotRange) {
                if (hs.added) return;
                hs.added = true;
                this.panoViewer.addHotSpot(hs);
            } else {
                if (!hs.added) return;
                hs.added = false;
                this.panoViewer.removeHotSpot(hs.id);
            }
        });

        setTimeout(() => {
            const hotspotsToggleButton = document.querySelector('.pnlm-hotspots-toggle i') as HTMLDivElement;
            hotspotsToggleButton.style.opacity = hotspotRange === 0 ? '0.5' : '1';

            Array.from(document.querySelectorAll('[data-hotspot-range]')).forEach((el: HTMLButtonElement) => el.classList.remove('active'));
            (document.querySelector(`[data-hotspot-range="${hotspotRange}"]`) as HTMLButtonElement)?.classList.add('active');
        }, 100);
    };

    initPanAndZoom = () => {
        if (this.panoViewer) {
            this.panoViewer.destroy();
            this.panoViewer = null;
        }
        if (!this.imageViewer) {
            this.imageViewer = new ImageViewer(this.imgContainerEl, {
                // listeners: { onImageLoaded: data => { } }
            });
        }
        this.imageViewer.load(ImagesService.getFullImageUrl(this.props.image.id, '2k'));
    };

    toggleFullscreen() {
        if (this.panoViewer) {
            this.panoViewer.toggleFullscreen();
        } else if (this.imageViewer) {
            this.openOriginalImage();
        }
    }

    openOriginalImage() {
        ImagesService.openInNewTab(this.props.image.id, this.props.image.name);
    }
}

@observer
export class ImageViewerInfo extends React.Component<{
    image: IImage;
    position: 'top' | 'bottom';
    collapsible?: boolean;
    title?: React.ReactNode;
    headerActions?: React.ReactNode;
    children?: React.ReactNode;
}> {
    @observable isCollapsed = LStorage.getOrDefault('documentViewerInfoCollapsed', false);

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    toggleCollapsed = () => {
        this.isCollapsed = !this.isCollapsed;
        LStorage.setItem('documentViewerInfoCollapsed', this.isCollapsed);
    };

    render() {
        const { image, position, collapsible, title, headerActions } = this.props;

        let link = null;
        let splitLink = null;
        let description = null;
        const direction = image.direction ?? 0;
        if (!!image.description) {
            if (image.description.toLowerCase().indexOf('http://') == 0 || image.description.toLowerCase().indexOf('https://') == 0) {
                splitLink = image.description.split(' ');
                link = splitLink[0];
            } else if (image.description.toLowerCase().indexOf('www.') == 0) {
                splitLink = image.description.split(' ');
                link = 'http://' + splitLink[0];
            }
            if (splitLink && splitLink[1]) {
                description = splitLink.slice();
                description[0] = ' ';
                description = description.join(' ');
            }
        }

        return (
            <div
                id='image-popup-info'
                style={{
                    textAlign: 'left',
                    padding: position === 'bottom' ? '5px 20px 0' : '5px 20px 0',
                    flex: '0 0 auto',
                }}
            >
                <div className='image-collapsible-title' onClick={this.toggleCollapsed}>
                    <i className={!this.isCollapsed ? 'iki iki-chevron-down' : 'iki iki-chevron-up'}></i>
                    <div className='flex-row gap-3'>
                        <strong style={{ flexShrink: 0 }}>{getFilenameWithVersion(image)}</strong>
                        {this.isCollapsed && (
                            <>
                                {image.originalDate && (
                                    <span style={{ flex: 0 }}>
                                        <DateSpan timestamp={image.originalDate} />
                                    </span>
                                )}
                                {image.description && (
                                    <div style={{ flexShrink: 1 }} className='ellipsis' title={image.description}>
                                        {image.description}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {headerActions && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>{headerActions}</div>}
                </div>

                {!this.isCollapsed && (
                    <>
                        <div>
                            {image.description && link && (
                                <div style={{ maxHeight: 120, overflow: 'auto', wordBreak: 'break-word' }}>
                                    {kuura.getMessage('map.image_desc_js')}:{' '}
                                    <a href={link} target='_blank'>
                                        {splitLink[0]}
                                    </a>
                                    {description}
                                </div>
                            )}
                            {image.description && !link && (
                                <div
                                    style={{
                                        maxHeight: 120,
                                        overflow: 'auto',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {kuura.getMessage('map.image_desc_js')}: {image.description}
                                </div>
                            )}
                        </div>

                        <div>
                            <div>
                                {kuura.getMessage('map.image_date_js')}: {image.originalDate == null ? ' ?' : <DateTimeSpan timestamp={image.originalDate} />}
                            </div>
                        </div>

                        <div>
                            <div>
                                {kuura.getMessage('map.image_upload_date_js')}: {image.timestamp == null ? ' ?' : <DateTimeSpan timestamp={image.timestamp} timezone />}
                            </div>
                        </div>

                        <div className='flex-row ai-center' style={{ height: 21 }}>
                            <span>
                                {__('js_images.direction_degrees')}: {direction}
                            </span>
                            <span className='image-direction-arrow' style={{ transform: 'rotate(' + direction + 'deg)', verticalAlign: 'top' }}></span>
                        </div>

                        <div>
                            {image.creator && (
                                <div>
                                    {kuura.getMessage('map.image_creator_js')}: {image.creator.username}
                                </div>
                            )}
                        </div>

                        <div>
                            {image.alignmentName && (
                                <div
                                    style={{
                                        maxHeight: 120,
                                        overflow: 'auto',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {kuura.getMessage('js_files.attached_alignment')}: {image.alignmentName}
                                </div>
                            )}
                            {$.isNumeric(image.station) && (
                                <div title='On the main project alignment'>
                                    {kuura.getMessage('tablet.station_js')}: {image.station}
                                </div>
                            )}
                        </div>
                        {this.props.children}
                    </>
                )}
            </div>
        );
    }
}
